import {setTitle} from '@github-ui/document-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {FilterProps} from '@github-ui/filter'
import type {SidebarLink} from '@github-ui/saved-views'
import {getShortcutsWithSaved, savedViewRouteId, useShortcutDeletion, useShortcutEditor} from '@github-ui/saved-views'
import {useOrgAwareProviders} from '@github-ui/shared-filter-providers/providers/use-org-aware-providers'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {useNavigate} from '@github-ui/use-navigate'
import {useUser} from '@github-ui/use-user'
import {useLocation} from '@github-ui/react-router'
import {useEffect, useEffectEvent, useMemo, useState} from 'react'
import {PULLS_INBOX_PATH, PULLS_PATH, PULLS_SEARCH_PATH} from '../constants/routes'
import {
  usePullsDashboardActions,
  usePullsDashboardFetchedQuery,
  usePullsDashboardLocationSidebarLinkId,
  usePullsDashboardMaxPrAge,
  usePullsDashboardPayload,
  usePullsDashboardSavedViews,
  usePullsDashboardSelectedSidebarLink,
  useSetPullsDashboardSavedViews,
  useSetPullsDashboardSelectedSidebarLink,
} from '../contexts/PullsDashboardSurfaceStateContext'
import {isOnInboxPath} from '@github-ui/shared-dashboard-surface/helpers/is-on-inbox'
import {buildInboxFilterQuery, uniqueTerms} from '../helpers/inbox-filter-helpers'
import {deriveSidebarLink} from '@github-ui/shared-dashboard-surface/helpers/layout-helpers'
import {persistPullsPreferences} from '@github-ui/shared-dashboard-surface/helpers/pulls-preferences-api'
import {updateCachedSavedViews} from '../helpers/query-resolver'
import {getDefaultView, useSaveDefaultView} from '@github-ui/shared-dashboard-surface/hooks/use-save-default-view'
import {ReviewInvolvesFilterProvider} from './review-involves-filter-provider'
import type {PickerRepository} from '@github-ui/repos-picker'
import type {PullsInboxMaxAge as PullsInboxMaxAgeType} from '../types/routes-types'

import {useAnalytics} from '@github-ui/use-analytics'

/**
 * Hook to manage the state of the pulls dashboard surface layout, including query state,
 * saved views, and sidebar selection. Gets data from context rather than props.
 * Provides methods for updating and submitting queries, managing saved views, and selecting sidebar links.
 *
 * @returns An object containing:
 *   - savedViews: Array of saved views
 *   - selectedSidebarLink: Currently selected sidebar link
 *   - editing: Saved view editing state and methods
 *   - deleting: Saved view deletion state and methods
 *   - methods: Object with selectSidebarLink, createSavedView, editSavedView, and fetchResults functions
 *   - pullsFilterProps: Props for the pulls filter component
 */
export function usePullsDashboardSurfaceLayoutState() {
  const {sendAnalyticsEvent} = useAnalytics()

  const payload = usePullsDashboardPayload()
  const fetchedQuery = usePullsDashboardFetchedQuery()
  const locationSidebarLinkId = usePullsDashboardLocationSidebarLinkId()
  const maxPrAge = usePullsDashboardMaxPrAge()
  const {fetchResults, setMaxPrAge} = usePullsDashboardActions()
  const savedViews = usePullsDashboardSavedViews()
  const selectedSidebarLink = usePullsDashboardSelectedSidebarLink()
  const setSavedViews = useSetPullsDashboardSavedViews()
  const setSelectedSidebarLink = useSetPullsDashboardSelectedSidebarLink()

  const compactMode = payload.compactMode ?? false
  const sidebarLinks = useMemo(() => payload.sidebarLinks ?? [], [payload.sidebarLinks])

  const location = useLocation()
  const {pathname, search: locationSearch} = location
  const navigate = useNavigate()
  const isOnInbox = isOnInboxPath({pathname})

  const [query, setQuery] = useState(fetchedQuery)

  const [selectedRepos, setSelectedRepos] = useState<PickerRepository[]>(payload.selectedRepos ?? [])

  useEffect(() => {
    const title = selectedSidebarLink ? `${selectedSidebarLink.name} · Pull requests` : 'Pull requests'
    // Defer so TitleManager's effect (which reads meta.title from the query cache)
    // runs first on page load or navigation, then we override with the view-specific title
    const timeoutId = setTimeout(() => setTitle(title), 0)
    return () => clearTimeout(timeoutId)
  }, [locationSearch, selectedSidebarLink])

  const deriveSelectedSidebarLink = useEffectEvent(() =>
    deriveSidebarLink({
      sidebarLinkId: locationSidebarLinkId,
      basePath: PULLS_PATH,
      savedViews,
      sidebarLinks,
      location,
    }),
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuery(fetchedQuery)
    setSelectedSidebarLink(deriveSelectedSidebarLink())
    // Recompute on navigation (query, state, or path change)
  }, [fetchedQuery, locationSidebarLinkId, pathname, setSelectedSidebarLink])

  const {clearDefaultView} = useSaveDefaultView('pull-requests')

  const onShortcutDeleted = (deletedShortcut: SidebarLink) => {
    sendAnalyticsEvent('pull-requests.click', 'DELETE_SAVED_VIEW_BUTTON')

    const updated = savedViews.filter(shortcut => shortcut.id !== deletedShortcut.id)
    setSavedViews(updated)
    updateCachedSavedViews(updated)
    setSelectedSidebarLink(undefined)

    if (getDefaultView('pull-requests') === String(deletedShortcut.id)) {
      clearDefaultView()
    }
  }
  const deleting = useShortcutDeletion(onShortcutDeleted)

  const onShortcutSaved = (savedShortcut: SidebarLink) => {
    const updated = getShortcutsWithSaved(savedViews, savedShortcut)
    setSavedViews(updated)
    updateCachedSavedViews(updated)

    if (fetchedQuery !== savedShortcut.query) {
      fetchResults({q: savedShortcut.query, page: 1, sidebarLinkId: savedViewRouteId(savedShortcut)})
    } else {
      setSelectedSidebarLink(savedShortcut)
    }
  }
  const editing = useShortcutEditor({type: 'pull-requests', onShortcutSaved})

  function updateQuery(q: string) {
    setQuery(q)
    if (editing.shortcut) {
      editing.setValue('query', q)
    }
  }

  function navigateInboxWithQuery(rawQuery: string) {
    updateQuery(rawQuery)

    const searchParams = new URLSearchParams()
    if (rawQuery) {
      searchParams.set('filter', rawQuery)
    }
    searchParams.set('max_pr_age', maxPrAge)

    const search = searchParams.toString()
    navigate({pathname: PULLS_INBOX_PATH, search: search ? `?${search}` : ''})
  }

  function handleMaxPrAgeChange(age: PullsInboxMaxAgeType) {
    setMaxPrAge(age)

    const currentParams = new URLSearchParams(locationSearch)
    currentParams.set('max_pr_age', age)
    navigate({pathname: PULLS_INBOX_PATH, search: `?${currentParams.toString()}`})
  }

  async function kindlyDismissEditing() {
    if (editing.shortcut) {
      const dismissed = await editing.dismiss()
      if (!dismissed) return false
    }
    return true
  }

  async function selectSidebarLink(linkItem: SidebarLink) {
    if (!(await kindlyDismissEditing())) return
    deleting.reset()

    fetchResults({q: linkItem.query, page: 1, sidebarLinkId: savedViewRouteId(linkItem)})
  }

  async function createShortcut() {
    if (!(await kindlyDismissEditing())) return

    sendAnalyticsEvent('pull-requests.click', 'CREATE_SAVED_VIEW_BUTTON')

    setSelectedSidebarLink(undefined)
    deleting.reset()

    if (isOnInbox) {
      const defaultQuery = 'is:pr author:@me state:open archived:false sort:updated-desc'
      navigate({pathname: PULLS_SEARCH_PATH, search: `?q=${encodeURIComponent(defaultQuery)}`})
      editing.create(defaultQuery, undefined)
      updateQuery(defaultQuery)
    } else {
      editing.create(query, selectedSidebarLink)
    }
  }

  function editShortcut(shortcut: SidebarLink) {
    sendAnalyticsEvent('pull-requests.click', 'EDIT_SAVED_VIEW_BUTTON')

    deleting.reset()
    editing.edit(shortcut, query)
  }

  function updateShortcut(shortcut: SidebarLink) {
    sendAnalyticsEvent('pull-requests.click', 'UPDATE_SAVED_VIEW_BUTTON')

    deleting.reset()
    editing.updateQuery(shortcut, fetchedQuery)
  }

  const {currentUser} = useUser()

  const filterProviders = useFilterProviders({})

  // review-involves is a /pulls-only reviewer qualifier owned by our team, so it is composed in
  // here (behind its feature flag) rather than in the shared useFilterProviders hook. Injecting it
  // into the base list keeps it subject to the inbox repo-only filter below.
  const reviewInvolvesEnabled = isFeatureEnabled('review_involves_filter')
  const baseFilterProviders = useMemo(() => {
    if (!reviewInvolvesEnabled) return filterProviders
    const userFilterParam = {
      showAtMe: !!currentUser,
      currentUserLogin: currentUser?.login,
      currentUserAvatarUrl: currentUser?.avatarUrl,
    }
    return [...filterProviders, new ReviewInvolvesFilterProvider(userFilterParam, {filterTypes: {valueless: false}})]
  }, [reviewInvolvesEnabled, filterProviders, currentUser])

  const providers = useOrgAwareProviders({
    baseFilterProviders,
    effectiveQuery: query,
    isIssueFieldsGlobalSearchEnabled: false,
  })

  const routeAwareProviders = isOnInbox
    ? providers.filter(provider => 'key' in provider && provider.key === 'repo')
    : providers

  function selectInboxRepos(newRepos: PickerRepository[]) {
    const repoNames = uniqueTerms(
      newRepos.map(repo => (repo.ownerLogin ? `${repo.ownerLogin}/${repo.name}` : repo.name)),
    )

    sendAnalyticsEvent('inbox.click', 'REPO_SELECTOR_OPTION', {selectedReposCount: repoNames.length})

    persistPullsPreferences({selected_repos: repoNames})

    const nextQuery = buildInboxFilterQuery({repos: repoNames})
    setSelectedRepos(newRepos)
    navigateInboxWithQuery(nextQuery)
  }

  const pullsFilterProps: FilterProps = {
    id: 'pulls-dashboard-surface-filter',
    label: 'Search pull requests',
    variant: 'input',
    placeholder: 'Search pull requests',
    filterValue: query,
    providers: routeAwareProviders,
    onChange: updateQuery,
    onSubmit: filterQuery => {
      if (isOnInbox) {
        navigateInboxWithQuery(filterQuery.raw)
        return
      }

      fetchResults({
        q: filterQuery.raw,
        page: 1,
        sidebarLinkId: selectedSidebarLink ? savedViewRouteId(selectedSidebarLink) : '',
      })
    },
    settings: {groupAndKeywordSupport: true, showSuggestionsOnFocus: isFeatureEnabled('filter_suggestions_on_focus')},
  }

  return {
    compactMode,
    savedViews,
    selectedSidebarLink,
    sidebarLinks,
    editing,
    deleting,
    methods: {
      selectSidebarLink,
      createShortcut,
      editShortcut,
      updateShortcut,
      fetchResults,
    },
    pullsFilterProps,
    inboxPickerProps: {
      scope: {type: 'unbounded'} as const,
      selectedRepos,
      onSubmit: selectInboxRepos,
      handleMaxPrAgeChange,
    },
  }
}
